import React, { useState, useCallback } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent } from '@components/Card';
import { TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { DateInputBR } from '@components/DateInputBR';
import stockMovementReportService, {
  StockMovementReportResponse,
  StockMovementProduct,
  StockMovementOrderItem,
  StockMovementReceiptItem,
  StockMovementReconciliationRow,
} from '../../services/stockMovementReportService';

// ─── Helpers ─────────────────────────────────────────────────────

const getToday = () => new Date().toISOString().split('T')[0];

const formatNumber = (value: number | null | undefined) =>
  value != null ? value.toLocaleString('pt-BR') : '—';

// Sticky header cell: stays pinned to the top of the panel's scroll area so the column
// labels remain visible while the user scrolls through many rows.
const TH: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <th className={`sticky top-0 z-10 bg-slate-50 border-b border-slate-200 py-2 px-3 text-left text-xs font-medium text-slate-500 uppercase ${className}`.trim()}>{children}</th>
);

const DiffBadge: React.FC<{ value: number | null }> = ({ value }) => {
  if (value == null || value === 0) return <span className="text-slate-400">0</span>;
  const isPositive = value > 0;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${isPositive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
      {isPositive ? '+' : ''}{value.toLocaleString('pt-BR')}
    </span>
  );
};

// ─── Component ───────────────────────────────────────────────────

export const StockMovementReportView: React.FC = () => {
  const [date, setDate] = useState(getToday());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<StockMovementReportResponse | null>(null);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await stockMovementReportService.getReport(date);
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, [date]);

  return (
    <PermissionGuard permission="reports.stock_movements">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Movimentações de Estoque</h2>
            <p className="text-sm text-slate-500 mt-1">Relatório diário de movimentações de estoque com conciliação</p>
          </div>
        </div>

        {/* Filter */}
        <Card>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-end gap-4">
              <div className="flex-1 max-w-xs">
                <label className="block text-sm font-medium text-slate-700 mb-1">Data</label>
                <DateInputBR
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  value={date}
                  onChange={v => setDate(v)}
                />
              </div>
              <Button
                variant="primary"
                icon={<Search size={16} />}
                onClick={loadReport}
                disabled={loading}
              >
                {loading ? 'Carregando...' : 'Consultar'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-emerald-600" />
          </div>
        )}

        {report && !loading && (
          <>
            {/* Section 1: Products updated on the day */}
            <Card>
              <CardHeader title={`Produtos Atualizados (${report.products.length})`} />
              <CardContent className="overflow-auto max-h-[32rem]">
                {report.products.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">Nenhum produto atualizado nesta data.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="border-b border-slate-200">
                      <tr>
                        <TH>SKU</TH>
                        <TH>Descrição</TH>
                        <TH className="text-right">Estoque Anterior</TH>
                        <TH className="text-right">Estoque Atual</TH>
                        <TH className="text-right">Variação</TH>
                      </tr>
                    </thead>
                    <tbody>
                      {report.products.map((p: StockMovementProduct) => (
                        <TableRow key={p.sku}>
                          <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                          <TableCell>{p.description || '—'}</TableCell>
                          <TableCell className="text-right">{formatNumber(p.previous_stock_quantity)}</TableCell>
                          <TableCell className="text-right">{formatNumber(p.stock_quantity)}</TableCell>
                          <TableCell className="text-right">
                            <DiffBadge value={p.stock_quantity != null && p.previous_stock_quantity != null ? p.stock_quantity - p.previous_stock_quantity : null} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            {/* Section 2: Order items with stock updated (StockUpdatedTimestamp) on this day */}
            <Card>
              <CardHeader title={`Saídas — Pedidos com Estoque Lançado (${report.stock_updated_order_items.length} itens)`} />
              <CardContent className="overflow-auto max-h-[32rem]">
                <p className="text-xs text-slate-500 mb-3">
                  Itens de pedidos cujo estoque físico foi lançado nesta data. Impacta a coluna <strong>Estoque Físico</strong>.
                </p>
                {report.stock_updated_order_items.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">Nenhum item nesta data.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="border-b border-slate-200">
                      <tr>
                        <TH>SKU</TH>
                        <TH>Descrição</TH>
                        <TH className="text-right">Quantidade</TH>
                        <TH className="text-right">Pedido</TH>
                      </tr>
                    </thead>
                    <tbody>
                      {report.stock_updated_order_items.map((item: StockMovementOrderItem, idx: number) => (
                        <TableRow key={`stock-${idx}`}>
                          <TableCell className="font-mono text-xs">{item.sku}</TableCell>
                          <TableCell>{item.description || '—'}</TableCell>
                          <TableCell className="text-right text-red-600 font-medium">-{item.quantity}</TableCell>
                          <TableCell className="text-right">#{item.order_id}</TableCell>
                        </TableRow>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            {/* Section 3: Material Receipt items (entries) */}
            <Card>
              <CardHeader title={`Entradas — Recebimentos Conferidos (${report.receipt_items.length} itens)`} />
              <CardContent className="overflow-auto max-h-[32rem]">
                <p className="text-xs text-slate-500 mb-3">
                  Itens de conferências de entrada de produto finalizadas nesta data. Somam ao saldo de estoque.
                </p>
                {report.receipt_items.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">Nenhum item nesta data.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="border-b border-slate-200">
                      <tr>
                        <TH>SKU</TH>
                        <TH>Descrição</TH>
                        <TH className="text-right">Quantidade</TH>
                        <TH className="text-right">Recebimento #</TH>
                      </tr>
                    </thead>
                    <tbody>
                      {report.receipt_items.map((item: StockMovementReceiptItem, idx: number) => (
                        <TableRow key={`receipt-${idx}`}>
                          <TableCell className="font-mono text-xs">{item.sku}</TableCell>
                          <TableCell>{item.description || '—'}</TableCell>
                          <TableCell className="text-right text-emerald-600 font-medium">+{item.quantity}</TableCell>
                          <TableCell className="text-right">#{item.material_receipt_id}</TableCell>
                        </TableRow>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            {/* Section 5: Reconciliation */}
            <Card>
              <CardHeader title="Conciliação de Estoque" />
              <CardContent className="overflow-auto max-h-[32rem]">
                <p className="text-xs text-slate-500 mb-3">
                  Compara o saldo do dia anterior com o saldo atual, subtraindo saídas (pedidos) e somando entradas (recebimentos).
                  A coluna "Diferença" mostra divergências entre o saldo calculado e o saldo real do sistema.
                </p>
                {report.reconciliation.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">Nenhum dado para conciliação.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="border-b border-slate-200">
                      <tr>
                        <TH>SKU</TH>
                        <TH>Descrição</TH>
                        <TH className="text-right">Est. Físico Anterior</TH>
                        <TH className="text-right">(-) Saídas Físico</TH>
                        <TH className="text-right">(+) Entradas</TH>
                        <TH className="text-right">Calculado</TH>
                        <TH className="text-right">Est. Físico Atual</TH>
                        <TH className="text-right">Diferença</TH>
                      </tr>
                    </thead>
                    <tbody>
                      {report.reconciliation.map((row: StockMovementReconciliationRow) => (
                        <TableRow key={row.sku}>
                          <TableCell className="font-mono text-xs">{row.sku}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{row.description || '—'}</TableCell>
                          <TableCell className="text-right">{formatNumber(row.previous_stock_quantity)}</TableCell>
                          <TableCell className="text-right text-red-600">{row.order_items_stock_qty > 0 ? `-${row.order_items_stock_qty}` : '0'}</TableCell>
                          <TableCell className="text-right text-emerald-600">{row.receipt_items_qty > 0 ? `+${row.receipt_items_qty}` : '0'}</TableCell>
                          <TableCell className="text-right font-medium">{formatNumber(row.calculated_stock_quantity)}</TableCell>
                          <TableCell className="text-right">{formatNumber(row.current_stock_quantity)}</TableCell>
                          <TableCell className="text-right"><DiffBadge value={row.stock_quantity_diff} /></TableCell>
                        </TableRow>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default StockMovementReportView;
